import {
  Button as HeadlessButton,
  type ButtonProps as HeadlessButtonProps,
} from "@headlessui/react";
import { Link, type LinkProps } from "@tanstack/react-router";
import classNames from "classnames";
import {
  type ComponentRef,
  type ElementType,
  Fragment,
  type JSX,
  type ReactNode,
  type RefAttributes,
  forwardRef,
} from "react";
import type React from "react";

import { cn } from "@/lib/utils";

import Loading from "../loadings/Loading";

export type ButtonProps<T extends ElementType = "button"> = {
  as?: T;
  color?: keyof typeof variants;
  variant?: "default" | "error" | "warning" | "success";
  size?: keyof typeof sizes;
  square?: boolean;
  center?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  isLoading?: boolean;
  children?: ReactNode;
  padding?: boolean;
  link?: LinkProps;
  selected?: boolean;
} & Omit<HeadlessButtonProps<T>, "size" | "children">;

const sizes = {
  xs: "gap-1.5 text-xs font-semibold",
  sm: "gap-2 text-sm font-semibold",
  md: "gap-2 text-sm font-semibold",
  lg: "gap-2 font-semibold",
  xl: "gap-2 font-semibold",
  "2xl": "gap-2 text-lg font-semibold",
};

const paddings = {
  default: {
    xs: "py-1.5 px-3",
    sm: "py-[7px] px-3.5",
    md: "py-[9px] px-4",
    lg: "py-[9px] px-[18px]",
    xl: "py-[11px] px-5",
    "2xl": "py-[15px] px-7",
  },
  square: {
    xs: "p-1.5",
    sm: "p-2",
    md: "p-2.5",
    lg: "p-3",
    xl: "p-3.5",
    "2xl": "p-4",
  },
};

const variants = {
  primary: {
    default: {
      style: "bg-primary-600 text-white border border-primary-600",
      hover: "hover:bg-primary-700",
      focus: "focus:ring-3 focus:ring-primary-100",
      disabled:
        "disabled:bg-primary-200 disabled:border-primary-200 disabled:text-white disabled:cursor-not-allowed",
    },
    error: {
      style: "bg-error-600 text-white border border-error-600",
      hover: "hover:bg-error-700",
      focus: "focus:ring-3 focus:ring-error-100",
      disabled:
        "disabled:bg-error-200 disabled:border-error-200 disabled:text-white",
    },
    warning: {
      style: "bg-warning-600 text-white border border-warning-600",
      hover: "hover:bg-warning-700",
      focus: "focus:ring-3 focus:ring-warning-100",
      disabled:
        "disabled:bg-warning-200 disabled:border-warning-200 disabled:text-white",
    },
    success: {
      style: "bg-success-600 text-white border border-success-600",
      hover: "hover:bg-success-700",
      focus: "focus:ring-3 focus:ring-success-100",
      disabled:
        "disabled:bg-success-200 disabled:border-success-200 disabled:text-white",
    },
  },
  "secondary-gray": {
    default: {
      style:
        "bg-white text-gray-700 border border-gray-300 shadow-xs shadow-gray-900/5",
      hover: "hover:bg-gray-50 hover:text-gray-800",
      focus: "focus:ring-4 focus:ring-gray-100",
      disabled:
        "disabled:border-gray-200 disabled:text-gray-300 disabled:cursor-not-allowed",
    },
    error: {
      style:
        "bg-white text-error-700 border border-error-300 shadow-xs shadow-error-900/5",
      hover: "hover:bg-error-50 hover:text-error-800",
      focus: "focus:ring-4 focus:ring-error-100",
      disabled: "disabled:border-error-200 disabled:text-error-300",
    },
    warning: {
      style:
        "bg-white text-warning-700 border border-warning-300 shadow-xs shadow-warning-900/5",
      hover: "hover:bg-warning-50 hover:text-warning-800",
      focus: "focus:ring-4 focus:ring-warning-100",
      disabled: "disabled:border-warning-200 disabled:text-warning-300",
    },
    success: {
      style:
        "bg-white text-success-700 border border-success-300 shadow-xs shadow-success-900/5",
      hover: "hover:bg-success-50 hover:text-success-800",
      focus: "focus:ring-4 focus:ring-success-100",
      disabled: "disabled:border-success-200 disabled:text-success-300",
    },
  },
  "secondary-color": {
    default: {
      style:
        "bg-primary-50 text-primary-700 border border-primary-50 shadow-xs",
      hover:
        "hover:bg-primary-100 hover:border-primary-100 hover:text-primary-800",
      focus: "focus:ring-4 focus:ring-primary-100",
      disabled:
        "disabled:bg-primary-25 disabled:border-primary-25 disabled:text-primary-300",
    },
    error: {
      style: "bg-error-50 text-error-700 border border-error-50 shadow-xs",
      hover: "hover:bg-error-100 hover:border-error-100 hover:text-error-800",
      focus: "focus:ring-4 focus:ring-error-100",
      disabled:
        "disabled:bg-error-25 disabled:border-error-25 disabled:text-error-300",
    },
    warning: {
      style:
        "bg-warning-50 text-warning-700 border border-warning-50 shadow-xs",
      hover:
        "hover:bg-warning-100 hover:border-warning-100 hover:text-warning-800",
      focus: "focus:ring-4 focus:ring-warning-100",
      disabled:
        "disabled:bg-warning-25 disabled:border-warning-25 disabled:text-warning-300",
    },
    success: {
      style:
        "bg-success-50 text-success-700 border border-success-50 shadow-xs",
      hover:
        "hover:bg-success-100 hover:border-success-100 hover:text-success-800",
      focus: "focus:ring-4 focus:ring-success-100",
      disabled:
        "disabled:bg-success-25 disabled:border-success-25 disabled:text-success-300",
    },
  },
  "tertiary-gray": {
    default: {
      style: "text-gray-600",
      hover: "hover:bg-gray-50 hover:text-gray-700",
      focus: "focus:text-gray-600",
      disabled: "disabled:text-gray-300",
    },
    error: {
      style: "text-error-700",
      hover: "hover:bg-error-50 hover:text-error-700",
      focus: "",
      disabled: "disabled:text-error-300",
    },
    warning: {
      style: "text-warning-700",
      hover: "hover:bg-warning-50 hover:text-warning-700",
      focus: "",
      disabled: "disabled:text-warning-300",
    },
    success: {
      style: "text-success-700",
      hover: "hover:bg-success-50 hover:text-success-700",
      focus: "",
      disabled: "disabled:text-success-300",
    },
  },
  "tertiary-color": {
    default: {
      style: "text-primary-700",
      hover: "hover:bg-primary-50",
      focus: "",
      disabled: "disabled:text-primary-300",
    },
    error: {
      style: "text-error-700",
      hover: "hover:bg-error-50",
      focus: "",
      disabled: "disabled:text-error-300",
    },
    warning: {
      style: "text-warning-700",
      hover: "hover:bg-warning-50",
      focus: "",
      disabled: "disabled:text-warning-300",
    },
    success: {
      style: "text-success-700",
      hover: "hover:bg-success-50",
      focus: "",
      disabled: "disabled:text-success-300",
    },
  },
  "link-gray": {
    default: {
      style: "bg-transparent text-gray-600",
      hover: "hover:text-gray-700",
      focus: "",
      disabled: "disabled:text-gray-300",
    },
    error: {
      style: "bg-transparent text-error-700",
      hover: "hover:text-error-800",
      focus: "",
      disabled: "disabled:text-error-300",
    },
    warning: {
      style: "bg-transparent text-warning-700",
      hover: "hover:text-warning-800",
      focus: "",
      disabled: "disabled:text-warning-300",
    },
    success: {
      style: "bg-transparent text-success-700",
      hover: "hover:text-success-800",
      focus: "",
      disabled: "disabled:text-success-300",
    },
  },
  "link-color": {
    default: {
      style: "bg-transparent text-primary-700",
      hover: "hover:text-primary-800",
      focus: "",
      disabled: "disabled:text-gray-300",
    },
    error: {
      style: "bg-transparent text-error-700",
      hover: "hover:text-error-800",
      focus: "",
      disabled: "disabled:text-error-300",
    },
    warning: {
      style: "bg-transparent text-warning-700",
      hover: "hover:text-warning-800",
      focus: "",
      disabled: "disabled:text-warning-300",
    },
    success: {
      style: "bg-transparent text-success-700",
      hover: "hover:text-success-800",
      focus: "",
      disabled: "disabled:text-success-300",
    },
  },
};

const Button = forwardRef(function Button(
  {
    as,
    children,
    color = "primary",
    variant = "default",
    startIcon,
    endIcon,
    size = "md",
    className,
    isLoading = false,
    disabled = false,
    square = false,
    center = true,
    padding = true,
    link,
    selected,
    ...props
  }: ButtonProps,
  ref: React.Ref<HTMLElement>,
) {
  const Component = as || ("button" as ElementType);

  const Wrapper = link && !disabled ? Link : Fragment;
  const wrapperProps =
    link && !disabled
      ? {
          ...link,
          className: classNames(
            "flex",
            center && "items-center justify-center",
            sizes[size],
          ),
        }
      : {};

  return (
    <HeadlessButton
      as={Component}
      ref={ref}
      className={cn(
        "flex cursor-pointer rounded-lg text-center outline-0 transition duration-200 ease-in-out",

        center && "items-center justify-center",

        sizes[size],

        padding &&
          !["link-gray", "link-color"].includes(color) &&
          paddings[square ? "square" : "default"][size],

        ...Object.values(variants[color][variant]),

        isLoading && "cursor-not-allowed",
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      <Wrapper {...(wrapperProps as any)}>
        {isLoading && <Loading size="xs" />}
        {startIcon && !isLoading && <span>{startIcon}</span>}
        {children}
        {endIcon && !isLoading && <span>{endIcon}</span>}
      </Wrapper>
    </HeadlessButton>
  );
}) as <TTag extends ElementType = "button">(
  props: ButtonProps<TTag> & RefAttributes<ComponentRef<TTag>>,
) => JSX.Element;

export default Button;
